/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Reports failed API calls to the server (POST /app/errors), which folds them into the
 * error email digest. It exists for the failures the API cannot see for itself — no response
 * at all (network down, timeout) or a 502/504 from the Azure ingress — and for 5xx the API
 * did raise, which the server matches up by request id so they are counted once.
 *
 * Best effort: throttled, fire-and-forget, and never throws.
 */

import { isCancel, type AxiosError, type AxiosInstance } from 'axios'

import logger from '@/utils/logger'

const log = logger({ category: 'errorReporter' })

const REPORT_PATH = '/app/errors'
const SAME_FAILURE_MS = 60_000
const MAX_PER_MINUTE = 10

const lastReported = new Map<string, number>()
let recent: number[] = []

/** Only server-side failures and lost requests; never 4xx, cancellations, or our own reports. */
export const shouldReport = (error: AxiosError): boolean => {
  if (isCancel(error) || error.code === 'ERR_CANCELED') return false
  if ((error.config?.url ?? '').includes(REPORT_PATH)) return false
  const status = error.response?.status
  if (status !== undefined) return status >= 500
  // No response: report only when a request actually went out (not e.g. a missing token).
  return Boolean(error.request)
}

/** Same method+path+status at most once a minute; at most ten reports a minute overall. */
const throttled = (key: string, now: number): boolean => {
  recent = recent.filter((t) => now - t < 60_000)
  if (recent.length >= MAX_PER_MINUTE) return true
  const last = lastReported.get(key)
  if (last !== undefined && now - last < SAME_FAILURE_MS) return true
  lastReported.set(key, now)
  recent.push(now)
  return false
}

export const reportApiError = (client: AxiosInstance, error: AxiosError, now = Date.now()): void => {
  try {
    if (!shouldReport(error) || !localStorage.getItem('MEGA_API_TOKEN')) return
    const status = error.response?.status ?? 0
    const method = (error.config?.method ?? 'get').toUpperCase()
    const url = error.config?.url ?? ''
    const path = url.split('?')[0]
    if (throttled(`${method} ${path} ${String(status)}`, now)) return

    const headers = error.response?.headers as Record<string, unknown> | undefined
    const requestId = typeof headers?.['x-request-id'] === 'string' ? headers['x-request-id'] : undefined
    const body = error.response?.data as { message?: unknown } | undefined
    const message = typeof body?.message === 'string' ? body.message : error.message

    void Promise.resolve(
      client.post(REPORT_PATH, {
        status,
        method,
        url: path,
        ...(requestId ? { requestId } : {}),
        message: message.slice(0, 2000),
        page: window.location.pathname.slice(0, 500),
      })
    ).catch((ex: unknown) => {
      log.debug('could not report an API error: %o', ex)
    })
  } catch (ex) {
    log.debug('error reporter failed: %o', ex)
  }
}

/** For tests. */
export const resetErrorReporter = (): void => {
  lastReported.clear()
  recent = []
}
